// A Scrivener 3 package read in the browser (`ScrivenerProject`, SuiteImport): the `.scrivx` binder with its labels,
// statuses, section types, custom metadata, keywords and collections, and each item's files under `Files/Data/<UUID>/`.
// The package arrives as a `FileMap` — paths relative to the `.scriv` folder, bytes on request — made from a zip or a
// folder picker by the app, or from a directory in tests; nothing here touches the file system itself.
import { parseXML, type XMLNode } from './xmlTree.ts';
import { SCRIVENER_HISTORY_PATH, scrivenerHistoryDays, type ImportedDay } from '../importedHistory.ts';

/** The package's files: paths relative to the `.scriv` folder ("Files/Data/<UUID>/content.rtf"), forward slashes. */
export interface FileMap {
  paths(): string[];
  read(path: string): Uint8Array | undefined;
}

export class ScrivenerError extends Error {
  /** `projectTitle`: the project an `olderCopy` or `differentProject` refusal is about. */
  constructor(readonly kind: 'notAScrivenerProject' | 'scrivener2' | 'damagedBinder' | 'olderCopy' | 'differentProject', message: string, readonly projectTitle?: string) {
    super(message);
    this.name = 'ScrivenerError';
  }
}

export interface BinderItem {
  uuid: string;
  type: string;
  title: string;
  created: Date | null;
  modified: Date | null;
  labelID: string | null;
  statusID: string | null;
  sectionType: string | null;
  includeInCompile: boolean;
  customMetadata: Record<string, string>;
  /** The project's keyword ids on the item, in Scrivener's order. */
  keywordIDs: string[];
  children: BinderItem[];
  /** Script mode: the text is a screenplay laid out by paragraph formats. */
  isScript: boolean;
}

export const isRootFolder = (item: BinderItem) => ['DraftFolder', 'ResearchFolder', 'TrashFolder'].includes(item.type);

export interface ScrivOption { id: string; name: string; color?: string }
export interface CustomField { id: string; title: string; type: string; options: ScrivOption[] }
export interface Keyword { id: string; title: string; children: Keyword[] }
export interface ScrivCollection { id: string; title: string; binderUUIDs: string[] }
export interface Snapshot { title: string; date: Date; path: string }

/** Scrivener's "2024-01-02 10:00:00 +1000". */
export function parseScrivenerDate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2}) ([+-])(\d{2})(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const sign = m[7] === '-' ? -1 : 1;
  const offset = sign * (Number(m[8]) * 60 + Number(m[9]));
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])) - offset * 60_000;
  return new Date(utc);
}

const SAFE_IDENTIFIER = /^[A-Za-z0-9-]{1,64}$/;

export class ScrivenerPackage {
  readonly identifier: string;
  readonly title: string;
  readonly binder: BinderItem[];
  readonly labels: ScrivOption[];
  readonly statuses: ScrivOption[];
  readonly sectionTypes: ScrivOption[];
  readonly customFields: CustomField[];
  readonly keywords: Keyword[];
  /** Every keyword's title by id, the nested ones included. */
  readonly keywordTitles: Record<string, string>;
  readonly collections: ScrivCollection[];
  /** The name of the project's script format (`Settings/scriptformat.xml`): "Screenplay", "Stage Play (US)"…; null when
   *  it has none. */
  readonly scriptFormatName: string | null;
  /** Project ▸ Writing History (`Files/writing.history`): empty when Scrivener kept none, or it can't be read. */
  readonly writingHistory: ImportedDay[];
  private readonly pathSet: Set<string>;

  /** `title` is the `.scriv` folder's name without the extension (the zip's or the picker's). */
  constructor(readonly files: FileMap, title: string) {
    this.pathSet = new Set(files.paths());
    const scrivx = files.paths().find((p) => !p.includes('/') && p.endsWith('.scrivx'));
    if (!scrivx) throw new ScrivenerError('notAScrivenerProject', 'That isn’t a Scrivener project: no .scrivx file in it.');
    const version = files.read('Files/version.txt');
    if (version) {
      const n = parseInt(new TextDecoder().decode(version).trim(), 10);
      if (Number.isFinite(n) && n < 23) throw new ScrivenerError('scrivener2', `This project was saved by Scrivener 2 (format ${n}). Open and save it in Scrivener 3 first.`);
    }
    if (![...this.pathSet].some((p) => p.startsWith('Files/Data/'))) {
      throw new ScrivenerError('scrivener2', 'This project has no Files/Data folder: it was saved by Scrivener 2, or isn’t whole. Open and save it in Scrivener 3 first.');
    }
    let root: XMLNode;
    try { root = parseXML(files.read(scrivx)!); } catch (e) { throw new ScrivenerError('damagedBinder', `The binder couldn’t be read: ${(e as Error).message}`); }
    this.identifier = root.attr('Identifier') ?? scrivx;
    this.title = title;
    this.binder = (root.child('Binder')?.childrenNamed('BinderItem') ?? []).map(ScrivenerPackage.item);
    this.labels = (root.child('LabelSettings')?.child('Labels')?.childrenNamed('Label') ?? []).flatMap((l) => {
      const id = l.attr('ID');
      return id && id !== '-1' ? [{ id, name: l.text.trim(), color: l.attr('Color') }] : [];
    });
    this.statuses = (root.child('StatusSettings')?.child('StatusItems')?.childrenNamed('Status') ?? []).flatMap((s) => {
      const id = s.attr('ID');
      return id && id !== '-1' ? [{ id, name: s.text.trim() }] : [];
    });
    this.sectionTypes = (root.child('SectionTypes')?.child('TypeDefinitions')?.childrenNamed('Type') ?? []).flatMap((t) => {
      const id = t.attr('ID');
      return id ? [{ id, name: t.text.trim() }] : [];
    });
    this.customFields = (root.child('CustomMetaDataSettings')?.childrenNamed('MetaDataField') ?? []).flatMap((f) => {
      const id = f.attr('ID');
      if (!id) return [];
      const options = (f.child('ListOptions')?.childrenNamed('Option') ?? []).flatMap((o) => (o.attr('ID') ? [{ id: o.attr('ID')!, name: o.text.trim() }] : []));
      return [{ id, title: f.value('Title') ?? id, type: f.attr('Type') ?? 'Text', options }];
    });
    this.keywords = (root.child('Keywords')?.childrenNamed('Keyword') ?? []).flatMap(ScrivenerPackage.keyword);
    const titles: Record<string, string> = {};
    const collect = (list: Keyword[]) => { for (const k of list) { titles[k.id] = k.title; collect(k.children); } };
    collect(this.keywords);
    this.keywordTitles = titles;
    this.collections = (root.child('Collections')?.childrenNamed('Collection') ?? []).flatMap((c) => {
      if (c.attr('Type') !== 'Arbitrary' || !c.attr('ID')) return [];
      const uuids = (c.value('BinderUUIDs') ?? '').split(',').map((u) => u.trim()).filter(Boolean);
      return [{ id: c.attr('ID')!, title: c.value('Title') ?? 'Collection', binderUUIDs: uuids }];
    });
    let formatName: string | null = null;
    try {
      const format = files.read('Settings/scriptformat.xml');
      formatName = format ? parseXML(format).value('Name')?.trim() ?? null : null;
    } catch { formatName = null; }
    this.scriptFormatName = formatName;
    let history: ImportedDay[] = [];
    try {
      const bytes = files.read(SCRIVENER_HISTORY_PATH);
      history = bytes ? scrivenerHistoryDays(bytes) : [];
    } catch { history = []; }
    this.writingHistory = history;
  }

  private static keyword(element: XMLNode): Keyword[] {
    const id = element.attr('ID');
    if (!id) return [];
    return [{ id, title: (element.value('Title') ?? ''), children: (element.child('Children')?.childrenNamed('Keyword') ?? []).flatMap(ScrivenerPackage.keyword) }];
  }

  private static item(element: XMLNode): BinderItem {
    const meta = element.child('MetaData');
    const custom: Record<string, string> = {};
    for (const item of meta?.child('CustomMetaData')?.childrenNamed('MetaDataItem') ?? []) {
      const field = item.value('FieldID'), value = item.value('Value');
      if (field !== undefined && value !== undefined) custom[field] = value;
    }
    const clean = (v: string | undefined) => (v === undefined || v === '-1' ? null : v);
    return {
      uuid: element.attr('UUID') ?? crypto.randomUUID().toUpperCase(),
      type: element.attr('Type') ?? 'Text',
      title: element.value('Title') ?? '',
      created: parseScrivenerDate(element.attr('Created')),
      modified: parseScrivenerDate(element.attr('Modified')),
      labelID: clean(meta?.value('LabelID')),
      statusID: clean(meta?.value('StatusID')),
      sectionType: meta?.value('SectionType') ?? null,
      includeInCompile: meta?.value('IncludeInCompile') === 'Yes',
      customMetadata: custom,
      keywordIDs: (meta?.child('Keywords')?.childrenNamed('KeywordID') ?? []).map((k) => k.text.trim()).filter(Boolean),
      children: (element.child('Children')?.childrenNamed('BinderItem') ?? []).map(ScrivenerPackage.item),
      isScript: element.child('TextSettings')?.value('TextMode') === 'Script',
    };
  }

  // MARK: Files

  /** Binder UUIDs name folders inside the package, so only letters, digits and hyphens are taken. */
  dataDirectory(uuid: string): string | null { return SAFE_IDENTIFIER.test(uuid) ? `Files/Data/${uuid}` : null; }

  /** A file of the item, or undefined if it has none. */
  file(name: string, uuid: string): Uint8Array | undefined {
    const dir = this.dataDirectory(uuid);
    return dir ? this.files.read(`${dir}/${name}`) : undefined;
  }

  rtf(name: string, uuid: string): Uint8Array | undefined { return this.file(name, uuid); }

  synopsis(uuid: string): string | undefined {
    const data = this.file('synopsis.txt', uuid);
    return data ? new TextDecoder('utf-8', { fatal: false }).decode(data) : undefined;
  }

  commentsXML(uuid: string): Uint8Array | undefined { return this.file('content.comments', uuid); }

  /** The media file of a PDF, image, web archive or other non-text item. */
  mediaFile(uuid: string): string | null {
    const dir = this.dataDirectory(uuid);
    if (!dir) return null;
    const skip = new Set(['content.rtf', 'content.comments', 'content.styles', 'notes.rtf', 'synopsis.txt']);
    const prefix = dir + '/';
    for (const p of this.pathSet) {
      if (!p.startsWith(prefix)) continue;
      const name = p.slice(prefix.length);
      if (!name.includes('/') && name.startsWith('content.') && !skip.has(name)) return p;
    }
    return null;
  }

  snapshots(uuid: string): Snapshot[] {
    if (!SAFE_IDENTIFIER.test(uuid)) return [];
    const dir = `Snapshots/${uuid}.snapshots`;
    const index = this.files.read(`${dir}/index.xml`);
    if (!index) return [];
    let root: XMLNode;
    try { root = parseXML(index); } catch { return []; }
    return root.childrenNamed('Snapshot').flatMap((s) => {
      const dateString = s.value('Date');
      const date = parseScrivenerDate(dateString);
      if (!dateString || !date) return [];
      // "2022-06-18 14:20:27 +1000" is stored as "2022-06-18-14-20-27+1000.rtf".
      const fileName = dateString.replace(' +', '+').replace(' -', '-').replaceAll(' ', '-').replaceAll(':', '-') + '.rtf';
      const path = `${dir}/${fileName}`;
      return this.pathSet.has(path) ? [{ title: s.value('Title') ?? 'Untitled snapshot', date, path }] : [];
    }).sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  /** When anything in the project was last edited (the latest binder item modification). */
  get lastModified(): Date | null {
    const dates = this.allItems().map((i) => i.modified).filter((d): d is Date => d !== null);
    return dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;
  }

  /** Every binder item, depth first. */
  allItems(): BinderItem[] {
    const out: BinderItem[] = [];
    const walk = (items: BinderItem[]) => { for (const item of items) { out.push(item); walk(item.children); } };
    walk(this.binder);
    return out;
  }

  descendants(item: BinderItem): BinderItem[] {
    const out: BinderItem[] = [];
    const walk = (items: BinderItem[]) => { for (const i of items) { out.push(i); walk(i.children); } };
    walk(item.children);
    return out;
  }
}

/** A `FileMap` over `name → bytes`, the names as a zip or a folder picker gives them: the `.scriv` folder itself (and any
 *  folder it sits in) is stripped, so paths start at the package's own files. Returns the map and the package's title. */
export function fileMapFrom(entries: Iterable<[string, Uint8Array]>): { files: FileMap; title: string } | null {
  const list = [...entries].filter(([p]) => !p.endsWith('/') && !p.split('/').some((part) => part === '__MACOSX' || part.startsWith('.')));
  // The `.scrivx` names the package: whatever precedes it is the prefix to strip.
  const scrivx = list.find(([p]) => p.endsWith('.scrivx'));
  if (!scrivx) return null;
  const cut = scrivx[0].lastIndexOf('/');
  const prefix = cut >= 0 ? scrivx[0].slice(0, cut + 1) : '';
  const folder = prefix.split('/').filter(Boolean).pop();
  const title = (folder ?? scrivx[0]).replace(/\.scrivx?$/i, '');
  const map = new Map<string, Uint8Array>();
  for (const [p, bytes] of list) if (p.startsWith(prefix)) map.set(p.slice(prefix.length), bytes);
  return { files: { paths: () => [...map.keys()], read: (p) => map.get(p) }, title };
}
